import { Helmet } from 'react-helmet-async'
import { BreadcrumbsJsonLd } from '@/components/seo/Breadcrumbs'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { AppointmentCards } from '@/components/sections/appointment-cards'
import { getGeneralHealthAppointments } from '@/data/appointments'
import { CalendarPlus } from 'lucide-react'
import { trackEvent } from '@/lib/analytics'
import { CTA } from '@/components/sections/cta'
import { LocationsSection } from '@/components/sections/locations-section'
import { AreasOfFocus } from '@/components/sections/areas-of-focus'
import { FreeConsultSection } from '@/components/sections/free-consult-section'
import { HowToBookSection } from '@/components/sections/how-to-book-section'
import { FAQFees } from '@/components/sections/faq-fees'
import { TableOfContents } from '@/components/sections/table-of-contents'

const tocItems = [
  { id: 'appointment-types-fees', title: 'Appointment Types & Fees' },
  { id: 'locations-section', title: 'Locations' },
  { id: 'areas-of-focus-section', title: 'Areas of Focus' },
  { id: 'free-consult-section', title: 'Free 15-Minute Phone Consult' },
  { id: 'how-to-book-section', title: 'How to Book' },
  { id: 'faq-fees-section', title: 'FAQ' },
]

export default function AppointmentsPage() {
  const openBooking = (source?: string) => {
    if (source) {
      trackEvent('book_appointment_click', { location: source })
    }
    if ((window as any).openBookingModal) {
      ;(window as any).openBookingModal(source)
    }
  }

  const generalHealthAppointments = getGeneralHealthAppointments()

  return (
    <main className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Appointments & Fees | Raintree Chinese Medicine | Noosa & Maroochydore | Sunshine Coast</title>
        <meta name="description" content="View appointment types and pricing for acupuncture and Chinese medicine with Dr Renee Knott at Raintree Chinese Medicine clinics in Noosa and Maroochydore on the Sunshine Coast. Book online." />
        <meta name="keywords" content="appointments, fees, acupuncture Noosa, acupuncture Maroochydore, Chinese medicine Sunshine Coast, Raintree Chinese Medicine, Dr Renee Knott, initial consultation, follow-up appointment, fertility acupuncture, women's health acupuncture" />
        <link rel="canonical" href="https://raintreechinesemedicine.com.au/appointments" />
        <meta property="og:title" content="Appointments & Fees | Raintree Chinese Medicine | Noosa & Maroochydore | Sunshine Coast" />
        <meta property="og:description" content="View appointment types and pricing for acupuncture and Chinese medicine with Dr Renee Knott at Raintree Chinese Medicine clinics in Noosa and Maroochydore on the Sunshine Coast. Book online." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://raintreechinesemedicine.com.au/appointments" />
        <meta property="og:image" content="https://tdjwqdpgnmzhfcqsfyjh.supabase.co/storage/v1/object/sign/images/general/Raintree-Chinese-Medicine-Renee.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8wYzVlMGM1NS1lYzI2LTRkMTYtOTc2MS1iZDQ1MWJhMGJiYjEiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJpbWFnZXMvZ2VuZXJhbC9SYWludHJlZS1DaGluZXNlLU1lZGljaW5lLVJlbmVlLnBuZyIsImlhdCI6MTc2NTE4MzkxOCwiZXhwIjoyMDIxNzkxOTE4fQ.-w4pg5Ka_Qt5M94A-DmuvJDiGP7a4DljS5sOLKvfAfE" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Appointments & Fees | Raintree Chinese Medicine | Noosa & Maroochydore | Sunshine Coast" />
        <meta name="twitter:description" content="View appointment types and pricing for acupuncture and Chinese medicine with Dr Renee Knott at Raintree Chinese Medicine clinics in Noosa and Maroochydore on the Sunshine Coast. Book online." />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Appointments & Fees',
            description: 'View appointment types and pricing for acupuncture and Chinese medicine services with Dr Renee Knott at Raintree Chinese Medicine clinics in Noosa and Maroochydore on the Sunshine Coast.',
            url: 'https://raintreechinesemedicine.com.au/appointments',
            mainEntity: {
              '@type': 'MedicalBusiness',
              name: 'Raintree Chinese Medicine',
              description: 'Acupuncture and Chinese medicine clinic offering women\'s health, fertility, pregnancy and long-term care services.',
              address: [
                {
                  '@type': 'PostalAddress',
                  streetAddress: '31 Burgess Drive',
                  addressLocality: 'Tewantin',
                  addressRegion: 'QLD',
                  postalCode: '4565',
                  addressCountry: 'AU'
                },
                {
                  '@type': 'PostalAddress',
                  streetAddress: 'Ground Level 5/55 Plaza Parade',
                  addressLocality: 'Maroochydore',
                  addressRegion: 'QLD',
                  postalCode: '4558',
                  addressCountry: 'AU'
                }
              ],
              areaServed: [
                'Noosa',
                'Noosa Heads',
                'Noosaville',
                'Tewantin',
                'Maroochydore',
                'Sunshine Coast',
                'Buderim',
                'Bli Bli',
                'Cotton Tree',
                'Mooloolaba'
              ],
              medicalSpecialty: [
                'Women\'s Health',
                'Fertility Treatment',
                'Pregnancy Care',
                'Acupuncture',
                'Chinese Medicine',
                'Hormonal Health',
                'IVF Support'
              ]
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: 'Home',
                  item: 'https://raintreechinesemedicine.com.au'
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: 'Appointments & Fees',
                  item: 'https://raintreechinesemedicine.com.au/appointments'
                }
              ]
            }
          })}
        </script>
      </Helmet>
      <h1 className="sr-only">Appointments and Pricing</h1>
      <BreadcrumbsJsonLd />
      
      {/* Table of Contents */}
      <TableOfContents items={tocItems} />

      {/* 1. Appointments & Fees (intro) */}
      <section className="bg-[rgb(132,137,116)] py-16 lg:py-40 px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Mobile: Stack layout */}
            <div className="lg:hidden text-center">
              <h1 className="text-2xl md:text-3xl mb-4 text-white">
                Appointments & Fees
              </h1>
              <p className="text-white/90 sm:w-2/3 mx-auto mb-6 sm:mb-12 text-sm sm:text-base">
                Appointments with Dr Renee Knott are available at clinics in Noosa and Maroochydore. Choose the clinic that's easiest for you, then select an appointment type.
              </p>
              <div className="flex flex-col gap-3 mb-4 sm:w-1/2 mx-auto">
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('noosa_clinic')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Noosa Clinic
                  </span>
                </Button>
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('maroochydore_clinic')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Maroochydore Clinic
                  </span>
                </Button>
              </div>
              <Button
                variant="outline"
                className="w-full border-white text-white hover:bg-white/10 sm:w-1/2 mx-auto"
                onClick={() => openBooking('free_consult_header')}
              >
                <span className="inline-flex items-center gap-2">
                  <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                  Free 15-minute Phone Consult
                </span>
              </Button>
            </div>

            {/* Desktop: Side-by-side layout */}
            <div className="hidden lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
              <div>
                <h1 className="text-3xl lg:text-4xl mb-4 text-white">
                  Appointments & Fees
                </h1>
                <p className="text-white/90 text-base lg:text-lg">
                  Appointments with Dr Renee Knott are available at clinics in Noosa and Maroochydore. Choose the clinic that's easiest for you, then select an appointment type.
                </p>
              </div>
              <div className="flex flex-col gap-3 mx-auto px-4">
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('noosa_clinic')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Noosa Clinic
                  </span>
                </Button>
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('maroochydore_clinic')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Maroochydore Clinic
                  </span>
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-white text-white hover:bg-white/80"
                  onClick={() => openBooking('free_consult_header')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Free 15-minute Phone Consult
                  </span>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Appointment types & Fees */}
      <section id="appointment-types-fees" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl mb-4 text-[#3f4141] max-w-[900px] mx-auto">
              Appointment Types & Fees
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Appointments are available in both Noosa and Maroochydore clinics.<br/>
              If you're booking for the first time, please choose an Initial Consultation & Treatment.
            </p>
          </motion.div>
          <AppointmentCards appointments={generalHealthAppointments} onBook={openBooking} />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mt-8 lg:mt-12"
          >
            <p className="text-gray-600 max-w-2xl mx-auto mb-2">
            Choose the clinic that is easiest to reach, and the appointment type and length that suits your needs.
            </p>
            <p className="text-gray-600 max-w-2xl mx-auto">
              All current fees and availability are shown in the online booking system.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 2. Locations */}
      <LocationsSection onBook={(locationName) => {
        trackEvent('book_appointment_click', { location: `locations_section_${locationName.toLowerCase()}` })
        openBooking(`locations_section_${locationName.toLowerCase()}`)
      }} />

      {/* 4. CTA - reuse from HomePage */}
      <CTA />

      {/* 5. Areas of focus (5 service pillars) */}
      <AreasOfFocus 
        fromAppointmentsPage={true}
        sectionId="areas-of-focus-section"
        onBook={(areaId, areaTitle) => {
          trackEvent('book_appointment_click', { location: `area_of_focus_${areaId}`, area: areaTitle })
          setBookingSource(`area_of_focus_${areaId}`)
          setIsBookingOpen(true)
        }} 
      />

      {/* 6. Free 15-minute phone consult */}
      <FreeConsultSection />

      {/* 7. Fees & rebates */}
      {/* <FeesRebatesSection /> */}

      {/* 8. How to book */}
      <HowToBookSection />

      {/* 9. FAQ */}
      <FAQFees />
    </main>
  )
}
